// FP3-061 — Provider Workboard edit modal. Used both for create
// (when `item` is null) and edit. Inline status/priority chips on
// the row remain — this modal handles every OTHER field.

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SOURCE_OPTIONS, STATUS_OPTIONS, PRIORITY_OPTIONS } from "@/lib/workboardConstants";

const EMPTY = {
  student_name_text: "",
  grade: "",
  teacher_name: "",
  campus_text: "",
  source_type: "provider_note",
  status: "new",
  priority: "medium",
  referral_date: "",
  follow_up_date: "",
  due_date: "",
  next_step: "",
  notes: "",
};

export default function WorkboardItemModal({ open, onClose, item, onSave }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="workboard-item-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {open && (
          <WorkboardItemModalBody
            key={item?.id || "new"}
            item={item}
            onClose={onClose}
            onSave={onSave}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function WorkboardItemModalBody({ item, onClose, onSave }) {
  const isEdit = Boolean(item?.id);
  const [draft, setDraft] = useState(() => (item ? { ...EMPTY, ...item } : EMPTY));
  const [saving, setSaving] = useState(false);

  const update = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const onSubmit = async () => {
    if (!draft.student_name_text || !draft.student_name_text.trim()) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
          <DialogTitle>{isEdit ? "Edit workboard item" : "Add workboard item"}</DialogTitle>
          <DialogDescription>
            Scratch surface for students or tasks not yet on your active caseload. Not
            connected to Caseload, Wizard, Health, Schedule, or Reports.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div className="sm:col-span-2">
            <Label htmlFor="wb-name" className="text-[12px]">Student name <span className="text-[#B85C5C]">*</span></Label>
            <Input
              id="wb-name"
              data-testid="workboard-form-name"
              value={draft.student_name_text}
              onChange={(e) => update("student_name_text", e.target.value)}
              placeholder="e.g. Aiden K. (4th grade)"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="wb-grade" className="text-[12px]">Grade</Label>
            <Input
              id="wb-grade"
              data-testid="workboard-form-grade"
              value={draft.grade}
              onChange={(e) => update("grade", e.target.value)}
              placeholder="3"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="wb-teacher" className="text-[12px]">Teacher</Label>
            <Input
              id="wb-teacher"
              data-testid="workboard-form-teacher"
              value={draft.teacher_name}
              onChange={(e) => update("teacher_name", e.target.value)}
              placeholder="Ms. Patel"
              className="mt-1"
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="wb-school" className="text-[12px]">School / campus</Label>
            <Input
              id="wb-school"
              data-testid="workboard-form-school"
              value={draft.campus_text}
              onChange={(e) => update("campus_text", e.target.value)}
              placeholder="Lincoln Elementary"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-[12px]">Source / type</Label>
            <Select
              value={draft.source_type || "provider_note"}
              onValueChange={(v) => update("source_type", v)}
            >
              <SelectTrigger data-testid="workboard-form-source" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[12px]">Status</Label>
            <Select
              value={draft.status || "new"}
              onValueChange={(v) => update("status", v)}
            >
              <SelectTrigger data-testid="workboard-form-status" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[12px]">Priority</Label>
            <Select
              value={draft.priority || "medium"}
              onValueChange={(v) => update("priority", v)}
            >
              <SelectTrigger data-testid="workboard-form-priority" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="wb-refdate" className="text-[12px]">Referral / request date</Label>
            <Input
              id="wb-refdate"
              type="date"
              data-testid="workboard-form-referral-date"
              value={draft.referral_date || ""}
              onChange={(e) => update("referral_date", e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="wb-followup" className="text-[12px]">Follow-up date</Label>
            <Input
              id="wb-followup"
              type="date"
              data-testid="workboard-form-followup-date"
              value={draft.follow_up_date || ""}
              onChange={(e) => update("follow_up_date", e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="wb-due" className="text-[12px]">Due date</Label>
            <Input
              id="wb-due"
              type="date"
              data-testid="workboard-form-due-date"
              value={draft.due_date || ""}
              onChange={(e) => update("due_date", e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="wb-nextstep" className="text-[12px]">Next step</Label>
            <Input
              id="wb-nextstep"
              data-testid="workboard-form-nextstep"
              value={draft.next_step}
              onChange={(e) => update("next_step", e.target.value)}
              placeholder="e.g. Schedule articulation screener"
              className="mt-1"
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="wb-notes" className="text-[12px]">Notes</Label>
            <Textarea
              id="wb-notes"
              data-testid="workboard-form-notes"
              value={draft.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={4}
              placeholder="Anything you want to remember about this student or task."
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button
            data-testid="workboard-form-cancel"
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            data-testid="workboard-form-save"
            onClick={onSubmit}
            disabled={saving || !draft.student_name_text.trim()}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add item"}
          </Button>
        </DialogFooter>
    </>
  );
}
